/*
 * Client-side prepaid cancellation rules (THY-2081), mirroring iOS behaviour.
 * The backend only enforces the 24h `cancellationPeriodEndDate` cutoff; these
 * two stricter checks are intentionally duplicated on each client.
 */

export interface PrepaidCancellationRuleInput {
  isPrepaid: boolean;
  /** Appointment calendar date, venue-local, YYYY-MM-DD */
  appointmentDate: string | undefined;
  /** Latest closed fiscal day from GET /pos-status, YYYY-MM-DD, null when unknown or never closed */
  lastClosedDay: string | null;
  /** Today's date, venue-local, YYYY-MM-DD */
  today: string;
}

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A prepaid appointment can no longer be cancelled once its calendar day has
 * passed (venue timezone) or its fiscal day has been closed. Fiscal days close
 * sequentially, so "appointment's day is closed" is equivalent to
 * `appointmentDate <= lastClosedDay`. An early close seals the whole day, so a
 * future appointment on a closed day is blocked too.
 *
 * Fails open: a missing/unknown `lastClosedDay` never blocks. ISO date strings
 * compare correctly as plain strings.
 */
export function isPrepaidCancellationBlocked(input: PrepaidCancellationRuleInput): boolean {
  const { isPrepaid, appointmentDate, lastClosedDay, today } = input;

  if (!isPrepaid) {
    return false;
  }

  if (
    appointmentDate == null ||
    !ISO_DATE_PATTERN.test(appointmentDate) ||
    !ISO_DATE_PATTERN.test(today)
  ) {
    return false;
  }

  const treatmentEndDatePassed = appointmentDate < today;
  const fiscalDayClosed =
    lastClosedDay != null &&
    ISO_DATE_PATTERN.test(lastClosedDay) &&
    appointmentDate <= lastClosedDay;

  return treatmentEndDatePassed || fiscalDayClosed;
}
